import type { SingleAssetValueOverview } from '@einwert/valuation-api-client'
import {
    Alert,
    assembleAddressFormat,
    EmptyState,
    getStructureUsageTypesText,
    joinDefinedStrings,
    LoadingSkeleton,
    PageGridItem,
    SearchField,
    SelectableListItem,
} from 'ComponentLibrary'
import { AssetsData, type StepProps } from '../types'
import { useEffect, useState } from 'react'
import { Stack, styled, useTheme } from '@mui/material'

import { FunnelFooter } from 'ClientPortal/src/pages/Orders/CreateOrder/FunnelFooter/FunnelFooter'
import { isEmpty } from 'lodash'
import { HouseIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

export type AssetStepProps = {
    assetsData: AssetsData
} & StepProps

export function ExistingAssetSubStep({
    assetsData: { multiAssetValueOverviewData, isMultiAssetValueOverviewDataPending },
    order,
    activeStep,
    handleNext,
    handleBack,
    dispatch,
    isLastStep = false,
}: AssetStepProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage.asset' })
    const theme = useTheme()
    const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(order.asset_uuid)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const [filteredAssets, setFilteredAssets] = useState<SingleAssetValueOverview[]>(multiAssetValueOverviewData?.assets ?? [])

    useEffect(() => {
        setFilteredAssets(multiAssetValueOverviewData?.assets ?? [])
    }, [setFilteredAssets, multiAssetValueOverviewData])

    const onSubmit = () => {
        if (selectedAssetId) {
            const selectedAsset = multiAssetValueOverviewData?.assets.find((asset) => asset.uuid === selectedAssetId)?.address
            selectedAsset &&
                dispatch({
                    type: 'updateAssetId',
                    payload: {
                        assetId: selectedAssetId,
                        //remove once backend's address field is optional
                        address: selectedAsset,
                    },
                })
            handleNext()
        } else {
            setErrorMessage(t('noAssetSelected'))
        }
    }

    const onHandleBack = () => {
        if (selectedAssetId) {
            const selectedAsset = multiAssetValueOverviewData?.assets.find((asset) => asset.uuid === selectedAssetId)?.address
            selectedAsset &&
                dispatch({
                    type: 'updateAssetId',
                    payload: {
                        assetId: selectedAssetId,
                        //remove once backend's address field is optional
                        address: selectedAsset,
                    },
                })
        }
        handleBack()
    }

    const selectAsset = (assetId: string) => {
        setSelectedAssetId(assetId)
        setErrorMessage(undefined)
    }

    const filterBySearch = (searchField: string) => {
        const assets = multiAssetValueOverviewData?.assets ?? []
        setFilteredAssets(
            assets.filter((asset) => {
                const fullAddress = assembleAddressFormat(asset.address)
                return joinDefinedStrings([fullAddress, asset.customer_asset_reference]).toLowerCase().includes(searchField.toLowerCase())
            })
        )
    }

    if (isMultiAssetValueOverviewDataPending) {
        return (
            <Stack direction="column">
                <LoadingSkeleton count={5} height="70px" width="480px" />
            </Stack>
        )
    }

    return (
        <PageGridItem columnSpan={3} columnSpanXs={4}>
            <Stack direction="column" gap={theme.spacing(4)}>
                {isMultiAssetValueOverviewDataPending ||
                    (!isEmpty(multiAssetValueOverviewData?.assets) && (
                        <SearchField handleSearch={filterBySearch} size="medium" label={t('search')} fullWidth />
                    ))}
                {errorMessage && (
                    <Alert severity="error" size="small">
                        {errorMessage}
                    </Alert>
                )}
            </Stack>
            {!isMultiAssetValueOverviewDataPending && filteredAssets.length === 0 && (
                <EmptyState title={t('noMatchingAssets')} size="large" style={{ maxWidth: '480px' }} />
            )}
            <StyledAssetList>
                {filteredAssets.map(({ uuid, economic_units, address, customer_asset_reference }) => {
                    const usageTypes = economic_units.flatMap(({ structures }) => structures.map(({ usage_type }) => usage_type))
                    const usageTypeSubtitle = getStructureUsageTypesText(usageTypes)
                    const addressSubtitle = assembleAddressFormat(address)
                    const subtitle = joinDefinedStrings([usageTypeSubtitle, addressSubtitle], ' | ')
                    return (
                        <SelectableListItem
                            key={uuid}
                            selected={selectedAssetId === uuid}
                            onClick={() => selectAsset(uuid)}
                            icon={<HouseIcon />}
                            title={joinDefinedStrings([customer_asset_reference, address.street, address.house_number])}
                            subtitle={subtitle}
                        />
                    )
                })}
            </StyledAssetList>
            <FunnelFooter activeStep={activeStep} isLastStep={isLastStep} handleNext={onSubmit} handleBack={onHandleBack} />
        </PageGridItem>
    )
}

const StyledAssetList = styled('ul')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: 0,
    listStyle: 'none',
    padding: 0,
}))
